import { Icon } from "@zapier/zinnia-icons";
import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  BetaMenuTriggerSeparator,
  Button,
  Spinner,
  Text,
} from "@zapier/design-system";
import { useCallback, useRef, useState } from "react";

import { capitalizeFirstLetter } from "../../utils";
import { EDITOR_ITEM, VIEWER_ITEM } from "../../constants";
import {
  Permission,
  SelectablePermission,
  RoleDefinitions,
  PermissionObjectType,
} from "../../types";

import styles from "./ChangePermissionButton.module.css";

type Props = {
  assetType: PermissionObjectType;
  isLoading?: boolean;
  permission: Permission;
  changePermissionTo: (permission: SelectablePermission) => void;
  removePermission: () => void;
  roleDefinitions: RoleDefinitions;
  shouldShowRemoveAccess?: boolean;
  showEditorOption: boolean;
  showViewerOption: boolean;
  removeAccessLabel?: string;
  /** Host dialog element for portals when nested in ModalDialog. */
  portalContainer?: Element;
};

export default function ChangePermissionButton({
  assetType,
  isLoading,
  permission,
  changePermissionTo,
  removePermission,
  roleDefinitions,
  showEditorOption,
  showViewerOption,
  shouldShowRemoveAccess = true,
  removeAccessLabel = "Remove access",
  portalContainer,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  // Ref-based flag to prevent the menu closing when the user first clicks
  // "Remove access" — the item action fires before onOpenChange, so we can
  // set this synchronously and intercept the close in handleOpenChange.
  const skipNextCloseRef = useRef(false);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && skipNextCloseRef.current) {
      skipNextCloseRef.current = false;
      return;
    }
    setIsMenuOpen(open);
    if (!open) setIsConfirmingRemove(false);
  }, []);

  const handleRemoveAccess = useCallback(() => {
    if (isConfirmingRemove) {
      removePermission();
      setIsMenuOpen(false);
      setIsConfirmingRemove(false);
    } else {
      skipNextCloseRef.current = true;
      setIsConfirmingRemove(true);
      setTimeout(() => setIsConfirmingRemove(false), 5000);
    }
  }, [isConfirmingRemove, removePermission]);

  const removeAccessText = isConfirmingRemove
    ? `Remove ${assetType} access?`
    : removeAccessLabel;

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <button disabled={true} className={styles.buttonNoStyle}>
          <Spinner />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.roleChangeButtonWrapper}>
      <BetaMenuTriggerRoot isOpen={isMenuOpen} onOpenChange={handleOpenChange}>
        <Button className={styles.buttonNoStyle}>
          <Text type="SmallPrint2" color="GrayWarm9">
            {capitalizeFirstLetter(permission)}
          </Text>
          <Icon name="arrowDropdown" size={24} color="GrayWarm6" />
        </Button>
        {/* Nested ModalDialog: see RoleDropdown. */}
        <BetaMenuTriggerPopover
          isNonModal
          UNSTABLE_portalContainer={portalContainer}
        >
          <BetaMenuTriggerList aria-label="Change permission">
            {showEditorOption && (
              <BetaMenuTriggerItem
                id="editor"
                textValue={EDITOR_ITEM.label}
                onAction={() => changePermissionTo("editor")}
              >
                <div className={styles.itemContent}>
                  <Text type="Body2" color="inherit">
                    {EDITOR_ITEM.label}
                  </Text>
                  <Text type="SmallPrint1" color="inherit">
                    {roleDefinitions[EDITOR_ITEM.value]}
                  </Text>
                </div>
              </BetaMenuTriggerItem>
            )}
            {showViewerOption && (
              <BetaMenuTriggerItem
                id="viewer"
                textValue={VIEWER_ITEM.label}
                onAction={() => changePermissionTo("viewer")}
              >
                <div className={styles.itemContent}>
                  <Text type="Body2" color="inherit">
                    {VIEWER_ITEM.label}
                  </Text>
                  <Text type="SmallPrint1" color="inherit">
                    {roleDefinitions[VIEWER_ITEM.value]}
                  </Text>
                </div>
              </BetaMenuTriggerItem>
            )}
            {shouldShowRemoveAccess && (
              <>
                <BetaMenuTriggerSeparator />
                <BetaMenuTriggerItem
                  id="remove"
                  textValue={removeAccessText}
                  variant="danger"
                  onAction={handleRemoveAccess}
                >
                  <Text type="Body1" color="StatusErrorStrongest">
                    {removeAccessText}
                  </Text>
                </BetaMenuTriggerItem>
              </>
            )}
          </BetaMenuTriggerList>
        </BetaMenuTriggerPopover>
      </BetaMenuTriggerRoot>
    </div>
  );
}
